import type {Page} from '@PosterWhiteboard/page/page.class';
import {v4 as uuidv4} from 'uuid';
import {ITEM_CONTROL_DIMENSIONS, ItemBorderColor} from '@PosterWhiteboard/poster/poster-item-controls';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {ItemBorderObject} from '@PosterWhiteboard/classes/item-border.class';
import {BorderType, ItemBorder} from '@PosterWhiteboard/classes/item-border.class';
import {degreesToRadians, doPolygonsIntersect} from '@Utils/math.util';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {TableItem} from '@PosterWhiteboard/items/table-item/table-item.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {NUM_FRACTION_DIGITS} from '@PosterWhiteboard/common.types';
import type {MenuItem} from '@PosterWhiteboard/items/menu-item/menu-item.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import type {TextSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {FancyTextItem} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {TabsItem} from '@PosterWhiteboard/items/tabs-item/tabs-item.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {RGBA} from '@Utils/color.util';
import {applyAlphaToFabricColor, colorToRgbaArray} from '@Utils/color.util';
import type {VectorItem} from '@PosterWhiteboard/items/vector-item/vector-item.class';
import type {StickerItem} from '@PosterWhiteboard/items/sticker-item.class';
import type {QRCodeItem} from '@PosterWhiteboard/items/qr-code-item.class';
import {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {cloneAsAlignedImage} from '@Utils/fabric.util';
import {updateSidebarState} from '@Components/poster-editor/poster-editor-reducer';
import {removeItemLoading} from '@Libraries/item-loading.library';
import type {FabricObject, SerializedShadowOptions, TFiller} from '@postermywall/fabricjs-2';
import {ActiveSelection, ShadowOrGlowType, util} from '@postermywall/fabricjs-2';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {RectangleItem} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {LineItem} from '@PosterWhiteboard/items/line-item/line-item';
import type {ImageBackground} from '@PosterWhiteboard/page/background/image-background.class';
import {RESELECT_ITEM_EVENT_TYPE} from './item.types';
import type {BaseItemObject, CommonItemStyles, CopyableItemStyles, InitItemOpts, ItemObject, ItemsWithFonts, TFabricColor, TSerializedFabricColor} from './item.types';
import {AnimationType} from './item.types';
import {ITEM_TYPE, SHADOW_REFERENCE_DIMENSION} from './item.types';
import {AnimateItemType, ItemAnimation} from '@PosterWhiteboard/classes/item-animation.class';
import type {DeepPartial} from '@/global';
import round from 'lodash/round';
import {getStaticValuesForAnimatingItem} from '@PosterWhiteboard/items/item/item.library';
import {isAnimationApplicableOnItem} from '@PosterWhiteboard/animation/animation.class';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {convertFabricShadowObjectToShadowOrGlowOptionsObject, convertShadowOptionsObjectToFabricShadowObject} from '@Utils/fabric-shadow.util';

export type ItemFabricObject = FabricObject;

export abstract class Item extends CommonMethods {
  public abstract gitype: ITEM_TYPE;

  public uid: string;
  public page: Page;
  public idOriginalOwner?: number;
  public sourcePosterHashId: string;
  public version = 1;
  public clickableLink = '';
  public border: ItemBorder;
  public introAnimation: ItemAnimation;
  public outroAnimation: ItemAnimation;
  public onPageStartTime: number;
  public onPageEndTime: number;
  public groupId: string | null = null;

  protected _fabricObject!: FabricObject;

  private isInitialzed = false;
  private reinitingItem = false;
  private readonly boundToggleItemVisibility = this.updateItemVisibility.bind(this);

  public constructor(page: Page) {
    super();
    this.border = new ItemBorder(this);
    this.page = page;
    this.uid = uuidv4();
    this.onPageStartTime = 0;
    this.onPageEndTime = page.getDuration() ? page.getDuration() : 0;
    this.introAnimation = new ItemAnimation(this, AnimateItemType.INTRO_ANIMATION);
    this.outroAnimation = new ItemAnimation(this, AnimateItemType.OUTRO_ANIMATION);
    this.sourcePosterHashId = page.poster.hashedID;
    this.page.on('time:updated', this.boundToggleItemVisibility);
  }

  get fabricObject(): FabricObject {
    return this._fabricObject;
  }

  /**
   * Whether this item can safely be converted to svg and inserted in PDF or
   * needs to be rasterized. Defaults to false and will rasterize all items
   * unless overridden by a child class.
   * @returns {Promise<boolean>}
   */
  public async isPDFSafe(): Promise<boolean> {
    return false;
  }

  public getPageStartTime(): number {
    return this.page.getStartTime();
  }

  public getPageEndTime(): number {
    return this.page.getEndTime();
  }

  public async getFabricObjectsForPDF(): Promise<ItemFabricObject[]> {
    const objects = [];
    const isCompatible = await this.isPDFSafe();

    if (!isCompatible) {
      const options = this.isText() || this.isTranscript() || (this.isSlideshow() && this.slides.isFirstSlideNonEmptyText()) ? {expandBoundingBoxByFont: true} : {};
      objects.push(
        await cloneAsAlignedImage(
          this.fabricObject,
          {
            ...options,
            multiplier: this.page.poster.scaling.scale,
          },
          ['__PMWID']
        )
      );
    } else {
      objects.push(this.fabricObject);
    }

    return objects;
  }

  public hasClickableLink(): boolean {
    return !!this.clickableLink;
  }

  public isMotionItem(): this is VideoItem | StickerItem {
    return this.gitype === ITEM_TYPE.VIDEO || this.gitype === ITEM_TYPE.STICKER;
  }

  public isVector(): this is VectorItem {
    return this.gitype === ITEM_TYPE.VECTOR;
  }

  public isText(): this is TextItem {
    return this.gitype === ITEM_TYPE.TEXT;
  }

  public isTranscript(): this is TranscriptItem {
    return this.gitype === ITEM_TYPE.TRANSCRIPT;
  }

  public isQRItem(): this is QRCodeItem {
    return this.gitype === ITEM_TYPE.QR_CODE;
  }

  public canMoveInZIndex(): boolean {
    return true;
  }

  public isVideo(): this is VideoItem {
    return this.gitype === ITEM_TYPE.VIDEO;
  }

  public isImage(): this is ImageItem {
    return this.gitype === ITEM_TYPE.IMAGE;
  }

  public isSticker(): this is StickerItem {
    return this.gitype === ITEM_TYPE.STICKER;
  }

  public isFancyText(): this is FancyTextItem {
    return this.gitype === ITEM_TYPE.FANCY_TEXT;
  }

  public isSlideshow(): this is SlideshowItem {
    return this.gitype === ITEM_TYPE.SLIDESHOW;
  }

  public isTextSlide(): this is TextSlideItem {
    return this.gitype === ITEM_TYPE.TEXTSLIDE;
  }

  public isImageSlide(): this is ImageSlideItem {
    return this.gitype === ITEM_TYPE.IMAGESLIDE;
  }

  public isImageBackground(): this is ImageBackground {
    return this.gitype === ITEM_TYPE.IMAGEBACKGROUND;
  }

  public isVideoSlide(): this is VideoSlideItem {
    return this.gitype === ITEM_TYPE.VIDEOSLIDE;
  }

  public isRectangle(): this is RectangleItem {
    return this.gitype === ITEM_TYPE.RECTANGLE;
  }

  public isLine(): this is LineItem {
    return this.gitype === ITEM_TYPE.LINE;
  }

  public isSlideshowSlide(): this is SlideshowItem {
    return this.isTextSlide() || this.isMediaSlide();
  }

  public isMediaSlide(): this is ImageSlideItem | VideoSlideItem {
    return this.isImageSlide() || this.isVideoSlide();
  }

  public isMenu(): this is MenuItem {
    return this.gitype === ITEM_TYPE.MENU;
  }

  public isTableItem(): this is TableItem {
    return this.gitype === ITEM_TYPE.TABLE;
  }

  public isTab(): this is TabsItem {
    return this.gitype === ITEM_TYPE.TAB;
  }

  public isSchedule(): boolean {
    return this.isTableItem() && this.layoutStyle !== LayoutTypes.CUSTOM_TABLE_LAYOUT;
  }

  public hasEditableText(): this is ItemsWithFonts {
    return (
      this.gitype === ITEM_TYPE.TEXT ||
      this.gitype === ITEM_TYPE.MENU ||
      this.gitype === ITEM_TYPE.TRANSCRIPT ||
      this.gitype === ITEM_TYPE.TAB ||
      this.gitype === ITEM_TYPE.TABLE ||
      this.gitype === ITEM_TYPE.SLIDESHOW
    );
  }

  protected isVisible(): boolean {
    return true;
  }

  public getCopyableStyles(): CopyableItemStyles {
    return {
      opacity: this.fabricObject.opacity,
      shadow: this.fabricObject.shadow?.toObject() as SerializedShadowOptions,
      flipX: this.fabricObject.flipX,
      flipY: this.fabricObject.flipY,
      introAnimation: this.introAnimation.toObject(),
      outroAnimation: this.outroAnimation.toObject(),
    };
  }

  public createCommonStylesObject(copiedStyles: CopyableItemStyles, keysToIgnore: (keyof CommonItemStyles)[] = []): DeepPartial<ItemObject> {
    const stylesObj: DeepPartial<ItemObject> = {};
    const item = window.posterEditor?.whiteboard?.getItemForId(this.uid);
    const allowedKeys: (keyof CommonItemStyles)[] = ['shadow', 'border', 'flipX', 'flipY', 'opacity', 'scaleX', 'scaleY'];

    for (const key of allowedKeys) {
      if (!keysToIgnore.includes(key) && copiedStyles[key] !== undefined) {
        if (key === 'opacity') stylesObj.alpha = copiedStyles.opacity;
        if (key === 'shadow') stylesObj.shadow = copiedStyles.shadow;
        if (key === 'flipY') stylesObj.flipY = copiedStyles.flipY;
        if (key === 'flipX') stylesObj.flipX = copiedStyles.flipX;
        if (key === 'scaleX') stylesObj.scaleX = copiedStyles.scaleX;
        if (key === 'scaleY') stylesObj.scaleY = copiedStyles.scaleY;
        if (key === 'border' && copiedStyles.border) {
          stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.border);
        }

        if (item) {
          if (copiedStyles.introAnimation) {
            if (isAnimationApplicableOnItem(item, copiedStyles.introAnimation.type)) {
              stylesObj.introAnimation = copiedStyles.introAnimation;
            }
          }
          if (copiedStyles.outroAnimation) {
            if (isAnimationApplicableOnItem(item, copiedStyles.outroAnimation.type)) {
              stylesObj.outroAnimation = copiedStyles.outroAnimation;
            }
          }
        }
      }
    }

    return stylesObj;
  }

  protected getBorderFromCopiedStyles(border: ItemBorderObject): ItemBorderObject {
    let newBorderType = border.solidBorderType;
    if (
      (border.solidBorderType === BorderType.STROKE_BORDER && !this.isStrokeBorderSupported()) ||
      (border.solidBorderType === BorderType.ROUNDED_BORDER && !this.isRoundedBorderSupported())
    ) {
      newBorderType = BorderType.RECTANGLE_BORDER;
    }

    return {
      ...border,
      solidBorderType: newBorderType,
    };
  }

  protected isStrokeBorderSupported(): boolean {
    return false;
  }

  protected isRoundedBorderSupported(): boolean {
    return true;
  }

  public async pasteStyles(commonStylesObj: DeepPartial<ItemObject>): Promise<void> {
    await this.updateFromObject(commonStylesObj);
  }

  public async invalidate(reinitItem = false, shouldMaintainScaledDimensionsOnReinit = false): Promise<void> {
    if (reinitItem) {
      await this.reInitFabricObject(shouldMaintainScaledDimensionsOnReinit);
    }

    if (this.fabricObject.group === undefined) {
      await this.updateFabricObject();
    }

    if (!this.page.poster.mode.isGeneration()) {
      this.page.fabricCanvas.requestRenderAll();
    }
  }

  public getFillObject(): TSerializedFabricColor {
    return (Item.isSerializableFiller(this.fabricObject.fill) ? this.fabricObject.fill.toObject() : this.fabricObject.fill) as TSerializedFabricColor;
  }

  public static isSerializableFiller(filler: TFabricColor): filler is TFiller {
    return !!filler && typeof (filler as TFiller).toObject === 'function';
  }

  public updateFillColorOpacity(fillAlpha: number, undoable = true): void {
    const fillColorObject = this.toObject().fill;

    void this.updateFromObject(
      {
        fill: applyAlphaToFabricColor(fillColorObject, fillAlpha),
      },
      {
        undoable,
      }
    );
  }

  protected async updateFabricObjectFromObject(obj: DeepPartial<ItemObject>): Promise<void> {
    if (obj.left !== undefined || obj.top !== undefined) {
      if (obj.left !== undefined) {
        this.fabricObject.set('left', obj.left);
      }
      if (obj.top !== undefined) {
        this.fabricObject.set('top', obj.top);
      }
      this.fabricObject.setCoords();
    }

    if (obj.alpha !== undefined) {
      this.fabricObject.set('opacity', obj.alpha);
    }

    if (obj.lockMovementX !== undefined) {
      this.fabricObject.set('lockMovementX', obj.lockMovementX);
    }

    if (obj.lockMovementY !== undefined) {
      this.fabricObject.set('lockMovementY', obj.lockMovementY);
    }

    if (obj.lockRotation !== undefined) {
      this.fabricObject.set('lockRotation', obj.lockRotation);
    }

    if (obj.lockScalingX !== undefined) {
      this.fabricObject.set('lockScalingX', obj.lockScalingX);
    }

    if (obj.lockScalingY !== undefined) {
      this.fabricObject.set('lockScalingY', obj.lockScalingY);
    }

    if (obj.angle !== undefined) {
      this.fabricObject.set('angle', obj.angle);
    }

    if (obj.flipX !== undefined) {
      this.fabricObject.set('flipX', obj.flipX);
    }

    if (obj.flipY !== undefined) {
      this.fabricObject.set('flipY', obj.flipY);
    }

    if (obj.width !== undefined) {
      this.fabricObject.set('width', obj.width);
    }

    if (obj.height !== undefined) {
      this.fabricObject.set('height', obj.height);
    }

    if (obj.scaleX !== undefined) {
      this.fabricObject.set('scaleX', obj.scaleX);
    }

    if (obj.scaleY !== undefined) {
      this.fabricObject.set('scaleY', obj.scaleY);
    }

    if (obj.shadow !== undefined) {
      if (obj.shadow === null) {
        this.fabricObject.set('shadow', null);
      } else {
        const unserializedObj = await util.enlivenObjectEnlivables({
          shadow: {
            ...this.fabricObject.shadow,
            ...obj.shadow,
          },
        });

        this.fabricObject.set('shadow', unserializedObj.shadow);
      }
    }
  }

  public getFonts(_withVariation: boolean): string[] {
    return [];
  }

  public async updateLock(val: boolean, undoable = true): Promise<void> {
    await this.updateFromObject(
      {
        lockMovementX: val,
        lockMovementY: val,
        lockRotation: val,
        lockScalingX: val,
        lockScalingY: val,
      },
      {undoable}
    );
  }

  public async updateFromObject(
    obj: DeepPartial<ItemObject>,
    {
      updateRedux = true,
      applyVersionFixes = false,
      applyVersionFixesData = {},
      fireModifiedEvent = true,
      undoable = true,
      doInvalidate = true,
      checkForDurationUpdate = false,
      replayPosterOnUpdateDone = false,
    }: UpdateFromObjectOpts = {}
  ): Promise<void> {
    if (this.reinitingItem) {
      return;
    }

    try {
      const additionalOldValuesForReinit = this.getAdditionalOldValuesForReinit();
      const oldItemObject = this.toObject();

      this.copyVals(obj);

      const reinit = this.isInitialzed && this.itemObjectHasDestructiveChanges(oldItemObject, additionalOldValuesForReinit);
      if (reinit) {
        this.reinitingItem = true;
      }

      await this.updateFabricObjectFromObject(obj);

      if (applyVersionFixes) {
        await this.fixChanges(applyVersionFixesData);
      }

      if (doInvalidate || reinit) {
        await this.invalidate(reinit, this.shouldMaintainScaledDimensionsOnReinit(oldItemObject, additionalOldValuesForReinit));
      }
    } finally {
      if (this.reinitingItem) {
        this.reinitingItem = false;
      }
    }

    await this.onItemUpdatedFromObject();

    if (checkForDurationUpdate) {
      this.checkItemForPageDurationUpdate();
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
    if (replayPosterOnUpdateDone) {
      await this.page.poster.replayPage(this.page.hashedID);
    }
  }

  protected async onItemUpdatedFromObject(): Promise<void> {}

  protected checkItemForPageDurationUpdate(): void {
    if (this.hasAnimation() || (!this.isChildItem() && this.isStreamingMediaItem())) {
      this.page.calculateAndUpdatePageDuration();
    }
  }

  public override copyVals(obj: DeepPartial<ItemObject>): void {
    const {border, introAnimation, outroAnimation, ...plainObj} = obj;
    super.copyVals(plainObj);
    this.border.copyVals(border);
    if (introAnimation) {
      this.introAnimation.copyVals(introAnimation);
    }
    if (outroAnimation) {
      this.outroAnimation.copyVals(outroAnimation);
    }
  }

  public hasAnimation(): boolean {
    return this.hasIntroAnimation() || this.hasOutroAnimation();
  }

  public hasIntroAnimation(): boolean {
    return this.introAnimation.type !== AnimationType.NONE;
  }

  public hasOutroAnimation(): boolean {
    return this.outroAnimation.type !== AnimationType.NONE;
  }

  protected getAdditionalOldValuesForReinit(): Record<string, any> {
    return {};
  }

  public async formatItemObjectWithDefaultValues(obj: DeepPartial<ItemObject>): Promise<DeepPartial<ItemObject>> {
    return {...obj};
  }

  protected itemObjectHasDestructiveChanges(_oldItemObject: ItemObject | BaseItemObject, _oldAdditionalValues: Record<string, any> = {}): boolean {
    return false;
  }

  protected shouldMaintainScaledDimensionsOnReinit(_oldItemObject: ItemObject | BaseItemObject, _oldAdditionalValues: Record<string, any> = {}): boolean {
    return true;
  }

  public toObject(): BaseItemObject {
    return {
      fill: this.getFillObject(),
      lockMovementX: this.fabricObject.lockMovementX,
      lockMovementY: this.fabricObject.lockMovementY,
      lockRotation: this.fabricObject.lockRotation,
      lockScalingX: this.fabricObject.lockScalingX,
      lockScalingY: this.fabricObject.lockScalingY,
      uid: this.uid,
      gitype: this.gitype,
      idOriginalOwner: this.idOriginalOwner,
      sourcePosterHashId: this.sourcePosterHashId ?? this.page.poster.hashedID,
      left: this.getX(),
      top: this.getY(),
      scaleX: this.getScaleX(),
      scaleY: this.getScaleY(),
      alpha: round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getObjectOpacity() : this.fabricObject.opacity, NUM_FRACTION_DIGITS),
      width: round(this.fabricObject.width, NUM_FRACTION_DIGITS),
      height: round(this.fabricObject.height, NUM_FRACTION_DIGITS),
      angle: round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getTotalAngle() : this.fabricObject.angle, NUM_FRACTION_DIGITS),
      erasable: this.fabricObject.erasable,
      border: this.border.toObject(),
      introAnimation: this.introAnimation.toObject(),
      outroAnimation: this.outroAnimation.toObject(),
      onPageStartTime: this.onPageStartTime,
      onPageEndTime: this.onPageEndTime,
      shadow: this.fabricObject.shadow ? (this.fabricObject.shadow.toObject() as SerializedShadowOptions) : null,
      clickableLink: this.clickableLink,
      flipX: this.fabricObject.flipX,
      flipY: this.fabricObject.flipY,
      version: this.version,
      groupId: this.groupId,
      ...getStaticValuesForAnimatingItem(this),
    };
  }

  public toObjectWithNewUids(): BaseItemObject {
    return {...this.toObject(), uid: uuidv4()};
  }

  public toDataURL(ignoreAlpha = true): string {
    const opacity = this.fabricObject.opacity;
    if (ignoreAlpha) {
      this.fabricObject.set('opacity', 1);
    }
    const data = this.fabricObject.toDataURL({
      format: 'png',
      quality: 1,
    });
    if (ignoreAlpha) {
      this.fabricObject.set('opacity', opacity);
    }
    return data;
  }

  public isStreamingMediaItem(): boolean {
    return false;
  }

  public isChildItem(): boolean {
    return false;
  }

  /**
   * Returns the duration of item to play
   * This differs from getDuration as this returns not the actual duration but the duration for which the item should be played on poster
   * i.e. for stickers this returns 5 seconds because we wanted stickers to loop for 5 secs
   * @return {number}
   */
  public getPlayDuration(): number {
    return this.getDuration();
  }

  public getDuration(): number {
    return 0;
  }

  public async play(): Promise<void> {
    // override if needed
  }

  public async pause(): Promise<void> {
    this.stopItemAnimation();
  }

  public async stop(): Promise<void> {
    this.stopItemAnimation();
  }

  public async seek(_time: number): Promise<void> {
    // override if needed
  }

  public async initFabricObject(opts: InitItemOpts = {}): Promise<void> {
    await this.onBeforeItemInitialize();
    const fabricObject = await this.getFabricObjectForItem(opts);
    this.setFabricObject(fabricObject);
    await this.onItemInitialized();
  }

  protected setFabricObject(fabricObject: ItemFabricObject): void {
    this._fabricObject = fabricObject;
    this.fabricObject.__PMWID = this.uid;
    this.initEvents();
    this.updateItemDimensions();
  }

  protected updateItemDimensions(): void {}

  protected initEvents(): void {
    this.fabricObject.on('modified', this.onFabricObjectModified.bind(this));
    this.fabricObject.on('mousedblclick', this.onDoubleClickItem.bind(this));
  }

  private isSelectedItemTextSlideOrText(): boolean {
    if (!this.page.isSingleItemSelected()) {
      return false;
    }

    const selectedItem = this.page.getSelectedItems()[0];
    return !!(selectedItem.isText() || (selectedItem.isSlideshow() && selectedItem.getSelectedSlide().isTextSlide()));
  }

  private openEditingSidebar(): void {
    if (this.isSelectedItemTextSlideOrText()) {
      return;
    }
    if (window.PMW.redux.store.getState().posterEditor.isSidebarSmall) {
      window.PMW.redux.store.dispatch(updateSidebarState(true));
    }
  }

  private onDoubleClickItem(): void {
    if (this.page.isSingleItemSelected()) {
      this.onItemDoubleClicked();
    }

    this.openEditingSidebar();
  }

  protected onDoubleTapItem(): void {
    this.openEditingSidebar();

    if (!window.PMW.redux.store.getState().posterEditor.isMobileVariant) {
      return;
    }
    if (this.page.isSingleItemSelected() && this.page.activeSelection.getActiveObjects()[0] === this.fabricObject) {
      this.onItemDoubleTapped();
    }
  }

  protected onItemDoubleTapped(): void {}

  protected onItemDoubleClicked(): void {}

  public isPremium(): boolean {
    return false;
  }

  public getBorderColor(): ItemBorderColor {
    return this.isStreamingMediaItem() ? ItemBorderColor.DYNAMIC_ITEM : ItemBorderColor.STATIC_ITEM;
  }

  protected onFabricObjectModified(): void {}

  protected async updateFabricObject(): Promise<void> {
    this.fabricObject.set({
      ...this.getCommonOptions(),
    });

    this.applyBorder();
    // TODO: Call setCoords only when top or left of the item changes. (http://fabricjs.com/fabric-gotchas)
    this.fabricObject.setCoords();
    this.applyShadowToFabricObject();
  }

  protected applyBorder(): void {
    this.fabricObject.set({
      ...this.border.getBorder(),
    });
  }

  protected applyShadowToFabricObject(): void {
    this.fabricObject.set('shadow', this.fabricObject.shadow);
  }

  protected getScalesAndShadowToMaintainScaledDimensions(newWidth: number, newHeight: number, opts: InitItemOpts = {}): Record<string, unknown> {
    const fabricOpts: Record<string, unknown> = {};

    const scaleX = opts.width && opts.scaleX ? (opts.width * opts.scaleX) / newWidth : undefined;
    const scaleY = opts.height && opts.scaleY ? (opts.height * opts.scaleY) / newHeight : undefined;

    if (scaleX) {
      fabricOpts.scaleX = scaleX;
    }
    if (scaleY) {
      fabricOpts.scaleY = scaleY;
    }

    if (opts.shadow) {
      fabricOpts.shadow = this.scaleShadow(opts.shadow, scaleX, scaleY, opts.scaleX, opts.scaleY);
    }

    return fabricOpts;
  }

  protected getCommonOptions(): Record<string, any> {
    return {
      ...this.getCommonOptionsForWebpage(),
      lockSkewingX: true,
      lockSkewingY: true,
      padding: this.getSelectorPadding(),
      hasControls: false,
      hasBorders: false,
    };
  }

  private getCommonOptionsForWebpage(): Record<string, any> {
    if (this.page.poster.mode.isWebpage() || this.page.poster.mode.isGeneration()) {
      return {
        selectable: false,
        hoverCursor: this.isSelectableInWebpage() ? 'pointer' : 'default',
        perPixelTargetFind: true,
      };
    }
    return {};
  }

  protected getInitOptionsForView(): Record<string, any> {
    return {};
  }

  protected async initFabricObjectForReinit(shouldMaintainScaledDimensionsOnReinit = false): Promise<void> {
    const initOpts: InitItemOpts = {};
    const {scaleX, scaleY} = this.fabricObject;
    if (shouldMaintainScaledDimensionsOnReinit) {
      initOpts.width = this.fabricObject.width;
      initOpts.height = this.fabricObject.height;
      initOpts.scaleX = this.fabricObject.scaleX;
      initOpts.scaleY = this.fabricObject.scaleY;
      initOpts.shadow = this.fabricObject.shadow?.toObject() as SerializedShadowOptions;
    }

    await this.initFabricObject(initOpts);
    if (!shouldMaintainScaledDimensionsOnReinit) {
      this.fabricObject.set({
        scaleX,
        scaleY,
      });
    }
  }

  protected async reInitFabricObject(shouldMaintainScaledDimensionsOnReinit = false): Promise<void> {
    const wasItemActive = this.fabricObject.isActive();
    const zIndex = this.page.items.getItemOrder(this.uid);
    const oldFabricObject = this.fabricObject;
    await this.beforeInitFabricObject();
    await this.initFabricObjectForReinit(shouldMaintainScaledDimensionsOnReinit);
    this.fabricObject.set({
      top: oldFabricObject.top,
      left: oldFabricObject.left,
      angle: oldFabricObject.angle,
      flipX: oldFabricObject.flipX,
      flipY: oldFabricObject.flipY,
      fill: oldFabricObject.fill,
    });
    if (this.page.fabricCanvas.contains(oldFabricObject)) {
      if (zIndex === undefined) {
        this.page.fabricCanvas.add(this.fabricObject);
      } else {
        this.page.fabricCanvas.insertAt(zIndex, this.fabricObject);
      }

      if (wasItemActive) {
        this.reselectItem(oldFabricObject);
      }
      this.page.fabricCanvas.remove(oldFabricObject);
    }
  }

  protected scaleShadow(shadow: SerializedShadowOptions, scaleX?: number, scaleY?: number, originalScaleX?: number, originalScaleY?: number): SerializedShadowOptions {
    const {offsetX, offsetY, blur} = shadow;
    const scaledShadow: SerializedShadowOptions = {...shadow};

    if (scaleX && originalScaleX) {
      scaledShadow.offsetX = offsetX * (originalScaleX / scaleX);
    }

    if (scaleY && originalScaleY) {
      scaledShadow.offsetY = offsetY * (originalScaleY / scaleY);
    }

    if (scaleX && scaleY && originalScaleX && originalScaleY) {
      const avgScale = (originalScaleX / scaleX + originalScaleY / scaleY) / 2;
      scaledShadow.blur = blur * avgScale;
    }

    return scaledShadow;
  }

  private reselectItem(oldFabricObject: ItemFabricObject): void {
    const activeObjects = this.page.activeSelection.getActiveObjects();
    const newActiveObjects = [];

    if (!activeObjects) {
      return;
    }

    for (const activeObject of activeObjects) {
      if (activeObject === oldFabricObject) {
        newActiveObjects.push(this.fabricObject);
      } else {
        newActiveObjects.push(activeObject);
      }
    }

    const reselectEvent = new MouseEvent(RESELECT_ITEM_EVENT_TYPE);
    this.page.activeSelection.discardActiveObject(reselectEvent);

    if (activeObjects.length > 1) {
      const sel = new ActiveSelection(newActiveObjects, {
        canvas: this.page.fabricCanvas,
      });
      this.page.activeSelection.setActiveObject(sel, reselectEvent);
    } else {
      this.page.activeSelection.setActiveObject(this.fabricObject, reselectEvent);
    }
  }

  public getSelectorPadding(): number {
    return ITEM_CONTROL_DIMENSIONS.PMW_CONTROL_PADDING;
  }

  protected hasParentGroupGraphicItem(): boolean {
    return !!this.fabricObject.group && !!getFabricObjectUID(this.fabricObject.group);
  }

  public getParentFabricObject(): ItemFabricObject {
    return this.fabricObject;
  }

  public doesfabricObjBelongtoItem(fabricObj: FabricObject): boolean {
    return this.fabricObject === fabricObj;
  }

  public onMoving(): void {
    this.page.poster.redux.updateReduxItemData(this.page.hashedID, this.uid, {
      left: this.getX(),
      top: this.getY(),
    });
  }

  public onRotating(): void {
    this.page.poster.redux.updateReduxItemData(this.page.hashedID, this.uid, {
      left: this.getX(),
      top: this.getY(),
      angle: this.getAngle(),
    });
  }

  public onScaling(): void {
    this.page.poster.redux.updateReduxItemData(this.page.hashedID, this.uid, {
      left: this.getX(),
      top: this.getY(),
      scaleX: this.getScaleX(),
      scaleY: this.getScaleY(),
    });
  }

  public getAngle(): number {
    return round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getTotalAngle() : this.fabricObject.angle, NUM_FRACTION_DIGITS);
  }

  public getX(): number {
    return round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getX() : this.fabricObject.left, NUM_FRACTION_DIGITS);
  }

  public getY(): number {
    return round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getY() : this.fabricObject.top, NUM_FRACTION_DIGITS);
  }

  public getScaleX(): number {
    return round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getObjectScaling().x : this.fabricObject.scaleX, NUM_FRACTION_DIGITS);
  }

  public getScaleY(): number {
    return round(this.fabricObject.group instanceof ActiveSelection ? this.fabricObject.getObjectScaling().y : this.fabricObject.scaleY, NUM_FRACTION_DIGITS);
  }

  protected abstract getFabricObjectForItem(opts?: InitItemOpts): Promise<FabricObject>;

  public isActive(): boolean {
    const parentItemPmwId = this.getParentFabricObject().__PMWID;
    const activeObjects = this.page.activeSelection.getActiveObjects();
    if (activeObjects) {
      for (const activeObject of activeObjects) {
        if (activeObject.__PMWID === parentItemPmwId) {
          return true;
        }
      }
    }
    return false;
  }

  public async init(opts: DeepPartial<ItemObject>, doInvalidate = true): Promise<void> {
    if (!this.isInitialzed) {
      await this.beforeInitFabricObject();
      const {width, height, scaleX, scaleY, shadow, ...itemObj} = opts;
      this.copyVals(itemObj);
      await this.initFabricObject({
        width,
        height,
        scaleX,
        scaleY,
        shadow: shadow as SerializedShadowOptions,
      });
      await this.updateFromObject(itemObj, {
        updateRedux: false,
        undoable: false,
        fireModifiedEvent: false,
        applyVersionFixes: true,
        applyVersionFixesData: {
          width,
          height,
          scaleX,
          scaleY,
        },
        doInvalidate,
      });
      this.isInitialzed = true;
    }
  }

  public getScaleForShadow(): number {
    const maxDimension = Math.max(this.fabricObject.height, this.fabricObject.width);
    return maxDimension / SHADOW_REFERENCE_DIMENSION;
  }

  protected async fixChanges(_applyVersionFixesData: Record<string, any>): Promise<void> {
    await this.fixAura();
  }

  protected async fixAura(): Promise<void> {
    if (this.page.poster.version < POSTER_VERSION.SHADOW_IMPROVEMENT) {
      const fabricShadow = this.fabricObject.shadow;
      if (!fabricShadow) {
        return;
      }

      if (fabricShadow.isShadow()) {
        const dropShadow = convertFabricShadowObjectToShadowOrGlowOptionsObject(fabricShadow.toObject() as SerializedShadowOptions, this.getScaleForShadow());
        if (!dropShadow) {
          return;
        }

        let newShadowColor = dropShadow.color;
        let newShadowAngle = dropShadow.angle;
        let newShadowDistance;

        const shadowScale = this.getScaleForShadow();

        if (fabricShadow.isCustomShadow()) {
          newShadowDistance = Math.round(
            ((this.getOldShadowDistance() + 2) * Math.cos(degreesToRadians(dropShadow.angle))) / (shadowScale * Math.cos(degreesToRadians(dropShadow.angle)))
          );
        } else {
          newShadowColor = [0, 0, 0, 1];
          newShadowColor[3] = fabricShadow.shadowOrGlowType === ShadowOrGlowType.LIGHT_SHADOW ? 0.25 : 0.5;
          newShadowAngle = 45;
          newShadowDistance = Math.round(this.getOldShadowDistance() / (shadowScale * Math.cos(degreesToRadians(dropShadow.angle))));
        }

        const dropShadowDistance = newShadowDistance;
        const dropShadowBlur = Math.round(this.getOldShadowBlur() / shadowScale);

        const newFabricShadow = convertShadowOptionsObjectToFabricShadowObject(
          {
            ...dropShadow,
            blur: dropShadowBlur,
            distance: dropShadowDistance,
            color: newShadowColor,
            angle: newShadowAngle,
          },
          this.getScaleForShadow()
        );

        const unserializedObj = await util.enlivenObjectEnlivables({
          shadow: newFabricShadow,
        });

        this.fabricObject.set('shadow', unserializedObj.shadow);
      }
    }
  }

  protected getOldShadowBlur(): number {
    return 2;
  }

  protected getOldShadowDistance(): number {
    return 6;
  }

  protected async beforeInitFabricObject(): Promise<void> {}

  public async onItemAddedToPage(): Promise<void> {
    // override if needed
  }

  protected async onItemInitialized(): Promise<void> {
    // override if needed
  }

  protected async onBeforeItemInitialize(): Promise<void> {
    // override if needed
  }

  public onRemove(): void {
    this.page.off('time:updated', this.boundToggleItemVisibility);
    removeItemLoading(this);
    this.introAnimation.animation.dispose();
    this.outroAnimation.animation.dispose();
    if (this.hasAnimation()) {
      this.page.items.updateItemsAnimation();
    }
  }

  public existsInPage(): boolean {
    if (this.page.items.getItem(this.uid)) {
      return true;
    }

    if (this.isImageSlide() || this.isVideoSlide() || this.isTextSlide()) {
      const slide = this as ImageSlideItem | VideoSlideItem | TextSlideItem;
      return slide.slideshow.existsInPage();
    }

    return false;
  }

  public async stretchToCanvasHorizontally(leaveSpace = false): Promise<void> {
    const cornerPoints = this.fabricObject.getCoords();
    const tlCorner = cornerPoints[0];
    let offset = 0;

    if (leaveSpace) {
      offset = this.page.poster.width * 0.2;
    }
    const newScaleX = ((this.page.poster.width - offset) * this.fabricObject.scaleX) / this.getBoundingBoxWidth();
    const newScaleY = (newScaleX / this.fabricObject.scaleX) * this.fabricObject.scaleY;

    // The left and top property of vo doesn't represent the tl corner appearing on the canvas if the graphic item is rotated. So we first find out the corner which has the least value of x and make it 0 so that
    // the whole graphic item remains on the canvas. After finding out the left most corner we set the left property of vo such that this corner gets x-axis value as 0
    let leftMostCornerPointIndex = 0;
    let newTlCornerXCoordinate = -this.getManualSelectorPadding();

    for (let i = 0; i < cornerPoints.length; i++) {
      if (cornerPoints[i].x < cornerPoints[leftMostCornerPointIndex].x) {
        leftMostCornerPointIndex = i;
      }
    }

    if (leftMostCornerPointIndex !== 0) {
      const selectorPaddingOffset = this.getManualSelectorPadding() * 2;
      newTlCornerXCoordinate = ((tlCorner.x - cornerPoints[leftMostCornerPointIndex].x - selectorPaddingOffset) * newScaleX) / this.fabricObject.scaleX;
    }

    await this.updateFromObject({
      left: newTlCornerXCoordinate,
      scaleX: newScaleX,
      scaleY: newScaleY,
    });
  }

  public async stretchToCanvasVertically(leaveSpace = false): Promise<void> {
    const cornerPoints = this.fabricObject.getCoords();
    const tlCorner = cornerPoints[0];
    let offset = 0;

    if (leaveSpace) {
      offset = this.page.poster.height * 0.2;
    }
    const newScaleY = ((this.page.poster.height - offset) * this.fabricObject.scaleY) / this.getBoundingBoxHeight();
    const newScaleX = (newScaleY / this.fabricObject.scaleY) * this.fabricObject.scaleX;

    // The left and top property of vo doesn't represent the tl corner appearing on the canvas if the graphic item is rotated. So we first find out the corner which has the least value of y and make it 0 so that
    // the whole graphic item remains on the canvas. After finding out the top most corner we set the top property of vo such that this corner gets y-axis value as 0
    let topMostCornerPoint = 0;
    let newTlCornerYCoordinate = -this.getManualSelectorPadding();

    for (let j = 0; j < cornerPoints.length; j++) {
      if (cornerPoints[j].y < cornerPoints[topMostCornerPoint].y) {
        topMostCornerPoint = j;
      }
    }

    if (topMostCornerPoint !== 0) {
      const selectorPaddingOffset = this.getManualSelectorPadding() * 2;
      newTlCornerYCoordinate = ((tlCorner.y - cornerPoints[topMostCornerPoint].y - selectorPaddingOffset) * newScaleY) / this.fabricObject.scaleY;
    }

    await this.updateFromObject({
      top: newTlCornerYCoordinate,
      scaleX: newScaleX,
      scaleY: newScaleY,
    });
  }

  public getBoundingBoxWidth(): number {
    const cornerPoints = this.fabricObject.getCoords();
    let leftMostCornerPointIndex = 0;
    let rightMostCornerPointIndex = 0;

    for (let i = 0; i < cornerPoints.length; i++) {
      if (cornerPoints[i].x < cornerPoints[leftMostCornerPointIndex].x) {
        leftMostCornerPointIndex = i;
      }
      if (cornerPoints[i].x > cornerPoints[rightMostCornerPointIndex].x) {
        rightMostCornerPointIndex = i;
      }
    }
    return cornerPoints[rightMostCornerPointIndex].x - cornerPoints[leftMostCornerPointIndex].x;
  }

  public getBoundingBoxHeight(): number {
    const cornerPoints = this.fabricObject.getCoords();
    let topMostCornerPointIndex = 0;
    let bottomMostCornerPointIndex = 0;

    for (let i = 0; i < cornerPoints.length; i++) {
      if (cornerPoints[i].y < cornerPoints[topMostCornerPointIndex].y) {
        topMostCornerPointIndex = i;
      }
      if (cornerPoints[i].y > cornerPoints[bottomMostCornerPointIndex].y) {
        bottomMostCornerPointIndex = i;
      }
    }
    return cornerPoints[bottomMostCornerPointIndex].y - cornerPoints[topMostCornerPointIndex].y;
  }

  /**
   * Returns the manually added selector padding.
   */
  public getManualSelectorPadding(): number {
    return 0;
  }

  public hasEditMode(): boolean {
    return false;
  }

  public getColors(): RGBA[] {
    const colors: RGBA[] = [];
    if (this.border.hasBorder()) {
      colors.push(this.border.solidBorderColor);
    }

    const shadow = this.fabricObject.shadow;

    if (shadow && !(shadow.isLightShadow() || shadow.isStrongShadow())) {
      colors.push(colorToRgbaArray(shadow.color));
    }

    return colors;
  }

  /**
   * Show item if time is within item's onPageStartTime and onPageEndTime or if it is meant to be visible always then show it even if current page isn't active
   * @param time
   */
  public isItemVisibleForTime(time: number): boolean {
    const isAfterStartTime = this.onPageStartTime <= time;
    const isBeforeEndTime = this.onPageEndTime >= time;

    if (isAfterStartTime && isBeforeEndTime) {
      return true;
    }

    const pageDuration = this.page.getDuration();
    const isVisibleTillEnd = this.onPageEndTime >= pageDuration;
    if (isVisibleTillEnd && time > pageDuration) {
      return true;
    }

    const isVisibleFromStart = this.onPageStartTime === 0;
    if (isVisibleFromStart && time < 0) {
      return true;
    }

    return false;
  }

  public updateItemVisibility(time: number): void {
    if (this.isSlideshowSlide() || this.isTranscript() || this.isImageBackground() || !this.fabricObject) {
      return;
    }
    const visible = this.isItemVisibleForTime(time);
    if (visible !== this.fabricObject.visible) {
      if (!visible) {
        this.stopItemAnimation();
      }
      this.fabricObject.set('visible', visible);
      this.page.fabricCanvas.requestRenderAll();
    }
  }

  public isItemVisibleOnPoster(): boolean {
    return (
      this.isVisible() && this.fabricObject.width > 0 && this.fabricObject.height > 0 && doPolygonsIntersect(this.fabricObject.getCoords(), this.page.poster.getCornerPointsArray())
    );
  }

  public stopItemAnimation(): void {
    if (this.introAnimation.animation.isAnimationPlaying) {
      this.introAnimation.animation.stop();
    }
    if (this.outroAnimation.animation.isAnimationPlaying) {
      this.outroAnimation.animation.stop();
    }
  }

  private isSelectableInWebpage = (): boolean => {
    return this.isSlideshow() || this.hasClickableLink();
  };

  public isOutlineDisabled(): boolean {
    if ((this.isText() || this.isTextSlide() || this.isTab()) && this.textStyles.isBold) {
      return true;
    }

    return !!((this.isMenu() || this.isTableItem()) && (this.isBold2 || this.textStyles.isBold));
  }

  public updateClickableLink(message: string, undoable = true): void {
    void this.updateFromObject(
      {
        clickableLink: message,
      },
      {undoable}
    );
  }

  public flipItemVertically(): void {
    if (this.fabricObject.lockMovementX) {
      return;
    }

    void this.updateFromObject({
      flipY: !this.fabricObject.get('flipY'),
    });
  }

  public flipItemHorizontally(): void {
    if (this.fabricObject.lockMovementX) {
      return;
    }

    void this.updateFromObject({
      flipX: !this.fabricObject.get('flipX'),
    });
  }

  public getFabricObjectAbsoluteLeft(): number {
    if (this.fabricObject.group) {
      return util.transformPoint({x: -this.fabricObject.width / 2, y: -this.fabricObject.height / 2}, this.fabricObject.calcTransformMatrix()).x;
    }

    return this.fabricObject.left;
  }

  public getFabricObjectAbsoluteTop(): number {
    if (this.fabricObject.group) {
      return util.transformPoint({x: -this.fabricObject.width / 2, y: -this.fabricObject.height / 2}, this.fabricObject.calcTransformMatrix()).y;
    }

    return this.fabricObject.top;
  }

  public getFabricObjectAbsoluteAngle(): number {
    if (this.fabricObject.group) {
      return util.qrDecompose(this.fabricObject.calcTransformMatrix()).angle;
    }

    return this.fabricObject.angle;
  }

  public getFabricObjectAbsoluteScaledWidth(): number {
    if (this.fabricObject.group) {
      return util.qrDecompose(this.fabricObject.calcTransformMatrix()).scaleX * this.fabricObject.width;
    }

    return this.fabricObject.getScaledWidth();
  }

  /**
   * @deprecated use fabric scaled width intead
   */
  public getScaledWidth(): number {
    return this.fabricObject.getObjectScaling().x * this.fabricObject.width;
  }

  /**
   * @deprecated use fabric scaled height intead
   */
  public getScaledHeight(): number {
    return this.fabricObject.getObjectScaling().y * this.fabricObject.height;
  }

  public getFabricObjectAbsoluteScaledHeight(): number {
    if (this.fabricObject.group) {
      return util.qrDecompose(this.fabricObject.calcTransformMatrix()).scaleY * this.fabricObject.height;
    }

    return this.fabricObject.getScaledHeight();
  }
}

export const getFabricObjectUID = (view: FabricObject): string => {
  return view.__PMWID;
};
